import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Eye, Bookmark, FileCode } from "lucide-react";
import verifiedBadge from "@/assets/verified-badge.png";
import { getTagColor } from "@/lib/tagColors";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { getCategoryImage } from "@/lib/categoryImages";
import { getCategoryGradient } from "@/lib/categoryGradients";
import { useAuth } from "@/hooks/useAuth";

interface MacroCardProps {
  id: string;
  slug: string;
  name: string;
  shortDesc: string;
  ownerHandle: string;
  ownerDisplayName: string;
  ownerVerified: boolean;
  riskScore: "low" | "medium" | "high" | "unknown";
  tags: string[];
  viewsCount: number;
  screenshotUrl?: string | null;
}

export const MacroCard = ({
  id,
  slug,
  name,
  shortDesc,
  ownerHandle,
  ownerDisplayName,
  ownerVerified,
  riskScore,
  tags,
  viewsCount,
  screenshotUrl
}: MacroCardProps) => {
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (user) {
      checkIfSaved(user.id);
    }
  }, [id, user]);
  const checkIfSaved = async (userId: string) => {
    const {
      data
    } = await supabase.from("saved_macros").select("id").eq("user_id", userId).eq("macro_id", id).maybeSingle();
    setIsSaved(!!data);
  };
  const handleSaveToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please sign in to save macros");
      return;
    }
    try {
      if (isSaved) {
        await supabase.from("saved_macros").delete().eq("user_id", user.id).eq("macro_id", id);
        setIsSaved(false);
        toast.success("Removed from saved");
      } else {
        await supabase.from("saved_macros").insert({
          user_id: user.id,
          macro_id: id
        });
        setIsSaved(true);
        toast.success("Saved!");
      }
    } catch (error) {
      console.error("Error toggling save:", error);
      toast.error("Failed to save");
    }
  };
  return <Link to={`/m/${slug}`} className="group block" data-macro-slug={slug} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={() => {
    // Store which macro was clicked for scroll restoration
    sessionStorage.setItem("clickedMacroSlug", slug);

    // Continue onboarding if user clicks card during tour
    const isOnboarding = sessionStorage.getItem('continueOnboardingStep');
    if (isOnboarding === '1') {
      sessionStorage.setItem('continueOnboardingStep', '2');
    }
  }}>
      <div className="relative bg-card rounded-2xl overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 h-full">
        {/* Preview Area with Overlay */}
        <div className={`h-48 flex items-center justify-center relative overflow-hidden rounded-t-2xl rounded-b-none border-2 border-muted border-b-0 ${screenshotUrl ? "bg-white dark:bg-muted" : getCategoryImage(tags[0]) ? getCategoryGradient(tags[0]) : "bg-muted"}`}>
          {screenshotUrl ? <img src={screenshotUrl} alt={name} className="w-full h-full object-cover" loading="lazy" /> : getCategoryImage(tags[0]) ? <img src={getCategoryImage(tags[0])!} alt={tags[0] || "Category"} className="w-32 h-32 object-contain" loading="lazy" /> : <FileCode className="w-20 h-20 text-muted-foreground" />}

          {/* Category Badge */}
          {tags.length > 0 && <div className="absolute top-3 left-3"></div>}

          {/* Save Button */}
          {user && (isHovered || isSaved) && <Button size="sm" variant="secondary" className="absolute top-3 right-3 h-8 w-8 p-0 rounded-full bg-background/90 backdrop-blur-sm hover:bg-background transition-all duration-200" onClick={handleSaveToggle}>
              <Bookmark className={`h-4 w-4 ${isSaved ? "fill-current" : ""}`} />
            </Button>}
        </div>

        {/* Content */}
        <div className="p-3 space-y-1 bg-muted">
          <h3 className="font-semibold text-xl line-clamp-1 text-foreground">{name}</h3>

          <p className="text-sm text-muted-foreground line-clamp-1 leading-relaxed min-h-[1.25rem]">{shortDesc}</p>

          <div className="flex items-center justify-between pt-1">
            <Link to={`/profile/${ownerHandle}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors font-medium">
              <span>{ownerDisplayName}</span>
              {ownerVerified && <img src={verifiedBadge} alt="Verified" className="h-3.5 w-3.5" loading="lazy" />}
            </Link>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" />
                {viewsCount.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>;
};